import React, { useCallback, useMemo } from 'react';
import { Select } from 'antd';
import { Label, Row } from '@ydistri/ds';
import { styled } from 'styled-components';
import { NO_VALUE } from '../../../lib/utils/utilsTypes';
import { selectFilterHandler } from '@ydistri/utils';
import { DefaultOptionType } from 'rc-select/lib/Select';

export interface SelectorOption {
  id: number;
  label?: string;
}

interface CommonSelectorProps {
  options: SelectorOption[];
  label: string;
  showSearch?: boolean;
  loading?: boolean;
}

interface SingleSelectorProps extends CommonSelectorProps {
  multiple?: false;
  value?: number;
  onChange?: (value: number) => void;
  /** Label for the synthetic "all values" entry shown at the top of a single-select. */
  noSelectionLabel?: string;
}

interface MultiSelectorProps extends CommonSelectorProps {
  multiple: true;
  value?: number[];
  onChange?: (value: number[]) => void;
}

export type GenericSelectorProps = SingleSelectorProps | MultiSelectorProps;

const SelectWrapper = styled(Select<number | number[]>)`
  min-width: 20rem;
`;

/**
 * Generic selector for a list of objects with an id and a label, prefixed with a Label.
 *
 * Mirrors AntD's polymorphic Select: by default it is a single-select that prepends a synthetic
 * "All values" option (value {@link NO_VALUE}); passing `multiple` turns it into a multi-select
 * whose empty array already means "no filter applied" (so it needs no synthetic option and reports
 * the selection as an array of ids). The `multiple` discriminant keeps `value`/`onChange` typed as
 * `number`/`number[]` respectively at the call site.
 * @param props see {@link SingleSelectorProps} / {@link MultiSelectorProps}
 * @constructor
 */
const GenericSelector: React.FC<GenericSelectorProps> = props => {
  const { options, label, showSearch, loading } = props;

  const changeHandler = useCallback(
    (value: number | number[]) => {
      if (props.multiple) {
        props.onChange?.(Array.isArray(value) ? value : []);
      } else if (typeof value === 'number') {
        //when cleared AntD calls onChange with undefined, which we ignore here because the clear is
        //routed through onClear -> NO_VALUE below
        props.onChange?.(value);
      }
    },
    [props],
  );

  const clearHandler = useCallback(() => changeHandler(NO_VALUE), [changeHandler]);

  const filterOption = useCallback(
    (input: string, option?: DefaultOptionType) => selectFilterHandler(input, option, false, false),
    [],
  );

  const mappedOptions = useMemo<DefaultOptionType[]>(
    () =>
      options.map(option => ({
        value: option.id,
        label: option.label ?? option.id.toString(),
      })),
    [options],
  );

  const noSelectionLabel = props.multiple ? undefined : props.noSelectionLabel;
  const allOptions = useMemo<DefaultOptionType[]>(() => {
    if (props.multiple) {
      return mappedOptions;
    }
    return [
      {
        value: NO_VALUE,
        label: `${noSelectionLabel ?? `All ${label}s`} (${options.length})`,
      },
      ...mappedOptions,
    ];
  }, [props.multiple, noSelectionLabel, label, options.length, mappedOptions]);

  const selectValue = props.multiple ? (props.value ?? []) : (props.value ?? NO_VALUE);
  const allowClear = props.multiple
    ? (props.value?.length ?? 0) > 0
    : (props.value ?? NO_VALUE) !== NO_VALUE;

  return (
    <Row $gap="1rem" $alignItems="center">
      <Label>{`${label}:`}</Label>
      <SelectWrapper
        mode={props.multiple ? 'multiple' : undefined}
        options={allOptions}
        id={`${label.toLowerCase()}-select`}
        onChange={changeHandler}
        value={selectValue}
        defaultValue={props.multiple ? undefined : NO_VALUE}
        placeholder={props.multiple ? `All ${label}s` : undefined}
        onClear={props.multiple ? undefined : clearHandler}
        allowClear={allowClear}
        showSearch={showSearch}
        filterOption={filterOption}
        popupMatchSelectWidth={false}
        loading={loading}
      />
    </Row>
  );
};

export default GenericSelector;
